import { useEffect, useState } from 'react';

// Diálogo de confirmação do design system — substitui window.confirm().
// Uso: if (!(await confirmDialog({ title: 'Excluir tarefa?' }))) return;

export type ConfirmOptions = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

type Listener = (pending: Pending | null) => void;

let pending: Pending | null = null;
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l(pending);
}

export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    // Se já houver um diálogo aberto, o novo o substitui cancelando o antigo.
    pending?.resolve(false);
    pending = { ...options, resolve };
    emit();
  });
}

function settle(ok: boolean) {
  pending?.resolve(ok);
  pending = null;
  emit();
}

export function ConfirmDialogHost() {
  const [current, setCurrent] = useState<Pending | null>(pending);
  useEffect(() => {
    listeners.add(setCurrent);
    return () => {
      listeners.delete(setCurrent);
    };
  }, []);

  useEffect(() => {
    if (!current) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') settle(false);
      if (e.key === 'Enter') settle(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current]);

  if (!current) return null;
  return (
    <div className="modal-overlay" onClick={() => settle(false)}>
      <div className="modal" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span>{current.title}</span>
        </div>
        {current.description && (
          <div className="modal-body text-sm text-[var(--t3)]">{current.description}</div>
        )}
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={() => settle(false)}>
            {current.cancelLabel ?? 'Cancelar'}
          </button>
          <button
            type="button"
            className={`btn ${current.danger === false ? 'btn-primary' : 'btn-danger'}`}
            onClick={() => settle(true)}
            autoFocus
          >
            {current.confirmLabel ?? 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}
